import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he' || i18n.language === 'ar';
  const { ref: statsRef, inView: statsVisible } = useInView({ triggerOnce: true, threshold: 0.2 });
  const [counts, setCounts] = useState([0, 0, 0, 0]); // years, areas, sat%, acc%

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!statsVisible) return;
    const targets = [30, 7, 95, 98];
    const timers = targets.map((target, idx) =>
      setInterval(() => {
        setCounts(prev => {
          if (prev[idx] >= target) {
            clearInterval(timers[idx]);
            return prev;
          }
          const next = [...prev];
          next[idx] = prev[idx] + 1;
          return next;
        });
      }, 50)
    );
    return () => timers.forEach(clearInterval);
  }, [statsVisible]);

  return (
    <section id="hero" className="relative h-screen flex overflow-hidden" style={{ direction: 'ltr' }}>
      {/* Blue background across entire section */}
      <div className="absolute inset-0 bg-[#cbe6f6] z-0" />

      {/* Desktop: Right-hand image | Mobile: Full background */}
      <div className="absolute right-0 top-0 w-full md:w-[60%] h-full z-10">
        {/* Mobile image */}
        <img
          src="/gallary/3.jpeg"
          alt="Hero Background"
          className="w-full h-full object-cover object-right md:hidden"
        />
        {/* Desktop image */}
        <img
          src="/gallary/2.jpeg"
          alt="Hero Background"
          className="hidden md:block w-full h-full object-cover object-right"
        />
        {/* Desktop gradient overlay */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            background: "linear-gradient(to left, transparent 60%, #cbe6f6)"
          }}
        />
        {/* Mobile dark overlay */}
        <div className="absolute inset-0 bg-black/40 md:hidden" />
      </div>

      {/* Desktop: Left panel | Mobile: Centered content */}
      <div className="absolute inset-0 md:left-0 md:top-0 md:w-[40%] md:h-full flex flex-col justify-center items-center md:items-start p-6 md:pl-12 z-20">
        {/* Title + Description */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center md:text-left max-w-md md:max-w-none"
        >
          <h1
            className={`text-4xl md:text-4xl lg:text-5xl font-bold mb-4 ${isRtl ? 'text-right' : 'text-left'} ${isRtl ? 'md:text-gray-800' : 'text-white md:text-gray-800'}`}
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            {t('hero.title').split('\n').map((line: string, idx: number, arr: string[]) => (
              <span key={idx}>
                {line}
                {idx < arr.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p
            className={`text-lg md:text-lg leading-relaxed ${isRtl ? 'text-right' : 'text-left'} ${isRtl ? 'md:text-gray-700' : 'text-white/90 md:text-gray-700'}`}
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            {t('hero.description')}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-6 w-full max-w-md md:max-w-none"
        >
          {[
            { value: counts[0], suffix: '+', label: t('stats.years'), target: 'about' },
            { value: counts[1], suffix: '+', label: t('stats.areas'), target: 'services' },
            { value: counts[2], suffix: '%', label: t('stats.satisfaction'), target: 'reviews' },
            { value: counts[3], suffix: '%', label: t('stats.accuracy'), target: 'reviews' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={statsVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="flex flex-col items-center md:items-start text-center md:text-left cursor-pointer hover:scale-105 transition-transform duration-200"
              onClick={() => scrollToSection(stat.target)}
            >
              <span className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                {stat.value}
                <span className="text-3xl md:text-3xl">{stat.suffix}</span>
              </span>
              <span className="mt-2 text-sm md:text-lg text-white/90 font-semibold">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;